import React from "react";
import { Link } from "react-router-dom";
import ScrollReveal from "../../components/ScrollReveal";
import "./IndustryPage.css";
import { getCopy, getCopyObject } from "../../utils/copyUtils";

interface IndustryProps {
  industryKey: string;
}

interface IndustryHero {
  title: string;
  description: string;
}

interface IndustryContent {
  title: string;
  paragraphs: string[];
  list_items: string[];
  conclusion: string;
}

interface IndustryBenefits {
  title: string;
  list_items: string[];
  cta: string;
}

const IndustryPage: React.FC<IndustryProps> = ({ industryKey }) => {
  // Get copy from JSON file
  const hero = getCopyObject<IndustryHero>(`industries.${industryKey}.hero`);
  const content = getCopyObject<IndustryContent>(`industries.${industryKey}.content`);
  const benefits = getCopyObject<IndustryBenefits>(`industries.${industryKey}.benefits`);

  if (!hero || !content || !benefits) {
    console.error(`Missing content for industry: ${industryKey}`);
    return <div>Content not found</div>;
  }

  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <ScrollReveal className="hero-content" direction="up" delay={200}>
            <h1 className="hero-title">{hero.title}</h1>
            <p className="hero-description">{hero.description}</p>
          </ScrollReveal>
        </div>
      </section>

      <main className="main">
        <div className="main__curved curved-ellipse"></div>
        <div className="main__curved-line curved-ellipse"></div>

        {/* Industry Section */}
        <section className="industry-section">
          <div className="industry-section-container container">
            <ScrollReveal direction="left" delay={300} className="industry-content">
              <h3 className="industry-content__title">{content.title}</h3>

              {content.paragraphs.map((paragraph, index) => (
                <p key={index} className="industry-content__text">
                  {paragraph}
                </p>
              ))}

              <ul className="industry-content__list">
                {content.list_items.map((item, index) => (
                  <li key={index} className="industry-content__list-item">
                    <div className="industry-content__list-item-icon complete-mark"></div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="industry-content__text">{content.conclusion}</p>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={400} className="demo-benefits">
              <div className="demo-benefits__image resonance-image glass-border-wrapper">
                <img src="/images/team-success.svg" alt={`${hero.title} Benefits`} />
              </div>

              <div className="demo-benefits__content">
                <h3 className="demo-benefits__title">{benefits.title}</h3>

                <ul className="demo-benefits__list">
                  {benefits.list_items.map((item, index) => (
                    <li key={index} className="demo-benefits__list-item">
                      <div className="demo-benefits__list-item-icon complete-mark"></div>
                      <span dangerouslySetInnerHTML={{ __html: item }}></span>
                    </li>
                  ))}
                </ul>
                <div className="industry-content__cta">
                  <Link to="/book-demo" className="btn btn-primary">
                    {benefits.cta}
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
    </>
  );
};

export default IndustryPage;
